import { useEffect, useMemo, useRef } from 'react'
import { addDays, toIsoDate, type CalendarEvent } from './calendarHelpers'
import { centerDateStripItem } from './dateStripScroll'
import type { CalendarRangeMode } from '../../types/schema'
import { Pressable } from '../Common/Pressable'

interface DateStripProps {
  focusedDate: Date
  events: CalendarEvent[]
  onDateClick: (date: Date) => void
  /** Drives what the strip enumerates: days (day), Monday-start week ranges
   * like 23-29 / 30-6 (week), or month names (month). */
  range?: CalendarRangeMode
}

// §8.4.8 — Single-letter labels collided (T for Tuesday AND Thursday, S for
// Saturday AND Sunday). Two-letter abbreviations disambiguate at the same
// pixel budget.
const WEEKDAY_INITIALS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']

const todayIso = () => toIsoDate(new Date())

/** Monday-start week (the shared startOfWeek helper is Sunday-based and is
 * left untouched — the strip's ranges are explicitly Mon-Sun). */
const startOfIsoWeek = (date: Date): Date => {
  const next = new Date(date.getFullYear(), date.getMonth(), date.getDate())
  const day = next.getDay()
  next.setDate(next.getDate() - ((day + 6) % 7))
  return next
}

type StripEntry = {
  key: string
  /** Small uppercase caption (weekday initial / "WK" / year). */
  caption: string
  /** Main label (day number / 23-29 / month name). */
  label: string
  /** Screen-reader label (day mode: full weekday + date; other modes fall
   * back to the visible caption + label). */
  ariaLabel?: string
  startDate: Date
  endDate: Date
  isFocused: boolean
  isToday: boolean
}

export const DateStrip = ({ focusedDate, events, onDateClick, range = 'day' }: DateStripProps) => {
  const focusedIso = toIsoDate(focusedDate)
  const today = todayIso()
  const activeRef = useRef<HTMLButtonElement | null>(null)
  const stripRef = useRef<HTMLDivElement | null>(null)

  const entries = useMemo<StripEntry[]>(() => {
    if (range === 'month') {
      return Array.from({ length: 12 }, (_, month) => {
        const start = new Date(focusedDate.getFullYear(), month, 1)
        const end = new Date(focusedDate.getFullYear(), month + 1, 0)
        const now = new Date()
        return {
          key: toIsoDate(start),
          caption: String(focusedDate.getFullYear()),
          label: start.toLocaleDateString(undefined, { month: 'long' }),
          startDate: start,
          endDate: end,
          isFocused: focusedDate.getMonth() === month,
          isToday: now.getFullYear() === focusedDate.getFullYear() && now.getMonth() === month,
        }
      })
    }

    if (range === 'week') {
      // Weeks covering the focused month, extended so partial edge weeks show
      // their cross-month ranges (23-29, 30-6, 7-13 …).
      const monthStart = new Date(focusedDate.getFullYear(), focusedDate.getMonth(), 1)
      const monthEnd = new Date(focusedDate.getFullYear(), focusedDate.getMonth() + 1, 0)
      const firstWeek = startOfIsoWeek(monthStart)
      const weeks: StripEntry[] = []
      const focusedWeekIso = toIsoDate(startOfIsoWeek(focusedDate))
      const todayWeekIso = toIsoDate(startOfIsoWeek(new Date()))
      for (let cursor = firstWeek; cursor.getTime() <= monthEnd.getTime(); cursor = addDays(cursor, 7)) {
        const end = addDays(cursor, 6)
        weeks.push({
          key: toIsoDate(cursor),
          caption: cursor.toLocaleDateString(undefined, { month: 'short' }),
          label: `${cursor.getDate()}-${end.getDate()}`,
          startDate: cursor,
          endDate: end,
          isFocused: toIsoDate(cursor) === focusedWeekIso,
          isToday: toIsoDate(cursor) === todayWeekIso,
        })
      }
      return weeks
    }

    // Day mode — every day of the focused month.
    const daysInMonth = new Date(focusedDate.getFullYear(), focusedDate.getMonth() + 1, 0).getDate()
    return Array.from({ length: daysInMonth }, (_, index) => {
      const day = new Date(focusedDate.getFullYear(), focusedDate.getMonth(), index + 1)
      const iso = toIsoDate(day)
      return {
        key: iso,
        caption: WEEKDAY_INITIALS[day.getDay()],
        // §8.4.10 — accessible label so screen readers announce a real name
        // instead of "tab" for each of the 28-31 unlabeled day buttons.
        ariaLabel: day.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }),
        label: String(day.getDate()),
        startDate: day,
        endDate: day,
        isFocused: iso === focusedIso,
        isToday: iso === today,
      }
    })
  }, [range, focusedDate, focusedIso, today])

  // Per-day event counts feed the indicator dot for whichever granularity is
  // showing (a week/month is dotted when any of its days has an event).
  const countByDay = useMemo(() => {
    const counts = new Map<string, number>()
    for (const event of events) {
      const cursor = new Date(event.startDate)
      const limit = new Date(event.endDate)
      if (Number.isNaN(cursor.getTime()) || Number.isNaN(limit.getTime())) continue
      while (cursor.getTime() <= limit.getTime()) {
        const key = toIsoDate(cursor)
        counts.set(key, (counts.get(key) ?? 0) + 1)
        cursor.setDate(cursor.getDate() + 1)
      }
    }
    return counts
  }, [events])

  const entryHasEvents = (entry: StripEntry): boolean => {
    for (let cursor = new Date(entry.startDate); cursor.getTime() <= entry.endDate.getTime(); cursor = addDays(cursor, 1)) {
      if ((countByDay.get(toIsoDate(cursor)) ?? 0) > 0) return true
    }
    return false
  }

  useEffect(() => {
    if (stripRef.current && activeRef.current) {
      centerDateStripItem(stripRef.current, activeRef.current)
    }
  }, [range, focusedIso])

  return (
    <div
      ref={stripRef}
      className={`calDateStrip calDateStrip--${range}`}
      style={{ maxWidth: 'calc(100vw - 48px)', minWidth: 0, overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}
      role="tablist"
      aria-label="Pick a date"
    >
      {entries.map((entry) => (
        <Pressable
          key={entry.key}
          ref={entry.isFocused ? activeRef : undefined}
          role="tab"
          aria-selected={entry.isFocused}
          aria-label={entry.ariaLabel ?? `${entry.caption} ${entry.label}`}
          className={`calDateStripDay${entry.isFocused ? ' isFocused' : ''}${entry.isToday ? ' isToday' : ''}`}
          onClick={() => onDateClick(entry.startDate)}
        >
          <span className="calDateStripWeekday">{entry.caption}</span>
          <span className="calDateStripNumber">
            {entry.label}
            {entryHasEvents(entry) && <span className="calDateStripDot" aria-hidden="true" />}
          </span>
        </Pressable>
      ))}
    </div>
  )
}
